import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Activity, Clock, Factory, AlertTriangle, CheckCircle2, TrendingUp, Loader2 } from 'lucide-react'
import { api } from '@/lib/api'

type OEE = {
  maquina_id: number; maquina_codigo: string; maquina_nome: string;
  capacidade_hora_kg: number;
  disponibilidade_pct: number; performance_pct: number; qualidade_pct: number; oee_pct: number;
  minutos_planejados: number; minutos_operando: number; minutos_parado: number;
  qtd_ok: number; qtd_refugo: number; horas_operando: number;
}

type ETA = {
  op_id: number; op_numero: number;
  produto_codigo: string | null; produto_descricao: string;
  maquina_codigo: string | null;
  quantidade: number; quantidade_produzida: number;
  tempo_estimado_min: number;
  inicio_previsto: string; fim_previsto: string;
  fim_previsto_pelo_user: string | null;
  em_atraso: boolean; status: string;
}

type Cap = {
  maquina_id: number; codigo: string; nome: string; tipo: string;
  capacidade_hora_kg: number; horas_dia: number;
  dias_uteis_semana: number; dias_uteis_mes: number;
  capacidade_dia_kg: number; capacidade_semana_kg: number; capacidade_mes_kg: number;
  disponibilidade_pct: number
  capacidade_real_dia_kg: number; capacidade_real_semana_kg: number; capacidade_real_mes_kg: number;
}

export default function IndicadoresIndustriaisPage() {
  const [dias, setDias] = useState(7)

  const { data: oee = [], isLoading: oeeL } = useQuery<OEE[]>({
    queryKey: ['ind-oee', dias],
    queryFn: async () => (await api.get(`/industrial/oee?dias=${dias}`)).data,
  })
  const { data: eta = [], isLoading: etaL } = useQuery<ETA[]>({
    queryKey: ['ind-eta'],
    queryFn: async () => (await api.get('/industrial/eta')).data,
  })
  const { data: cap = [], isLoading: capL } = useQuery<Cap[]>({
    queryKey: ['ind-cap-det'],
    queryFn: async () => (await api.get('/industrial/capacidade-detalhada')).data,
  })

  const oeeAvg = oee.length > 0 ? oee.reduce((s, x) => s + x.oee_pct, 0) / oee.length : 0
  const dispAvg = oee.length > 0 ? oee.reduce((s, x) => s + x.disponibilidade_pct, 0) / oee.length : 0
  const perfAvg = oee.length > 0 ? oee.reduce((s, x) => s + x.performance_pct, 0) / oee.length : 0
  const qualAvg = oee.length > 0 ? oee.reduce((s, x) => s + x.qualidade_pct, 0) / oee.length : 0

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Activity className="h-6 w-6 text-violet-600" /> Indicadores industriais
        </h1>
        <select
          value={dias}
          onChange={(e) => setDias(Number(e.target.value))}
          className="px-3 py-2 border border-ink-300 rounded text-sm"
        >
          <option value={1}>Hoje</option>
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
        </select>
      </div>

      {/* OEE GLOBAL */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KPICard label="OEE médio" valor={`${oeeAvg.toFixed(1)}%`} cor="violet" icon={Activity} />
        <KPICard label="Disponibilidade" valor={`${dispAvg.toFixed(1)}%`} cor="blue" icon={Clock} />
        <KPICard label="Performance" valor={`${perfAvg.toFixed(1)}%`} cor="emerald" icon={TrendingUp} />
        <KPICard label="Qualidade" valor={`${qualAvg.toFixed(1)}%`} cor="amber" icon={CheckCircle2} />
      </div>

      {/* OEE POR MÁQUINA */}
      <section className="bg-white rounded-lg border border-ink-200">
        <header className="px-4 py-3 border-b border-ink-200 flex items-center gap-2">
          <Factory className="h-4 w-4 text-violet-600" />
          <h2 className="font-semibold">OEE por máquina · últimos {dias} dia(s)</h2>
        </header>
        {oeeL ? <Loading /> : (
          <table className="w-full text-sm">
            <thead className="bg-ink-50">
              <tr className="text-left text-xs uppercase text-ink-600">
                <th className="px-3 py-2">Máquina</th>
                <th className="px-3 py-2 text-right">Disponibilidade</th>
                <th className="px-3 py-2 text-right">Performance</th>
                <th className="px-3 py-2 text-right">Qualidade</th>
                <th className="px-3 py-2 text-right">OEE</th>
                <th className="px-3 py-2 text-right">Operando</th>
                <th className="px-3 py-2 text-right">Refugo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {oee.map(m => (
                <tr key={m.maquina_id}>
                  <td className="px-3 py-2.5">
                    <div className="font-medium">{m.maquina_codigo}</div>
                    <div className="text-xs text-ink-500">{m.maquina_nome}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right"><Bar pct={m.disponibilidade_pct} cor="blue" /></td>
                  <td className="px-3 py-2.5 text-right"><Bar pct={m.performance_pct} cor="emerald" /></td>
                  <td className="px-3 py-2.5 text-right"><Bar pct={m.qualidade_pct} cor="amber" /></td>
                  <td className="px-3 py-2.5 text-right">
                    <span className={`text-base font-bold ${classByOee(m.oee_pct)}`}>{m.oee_pct.toFixed(1)}%</span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-ink-600">{m.horas_operando}h</td>
                  <td className="px-3 py-2.5 text-right text-xs">
                    {m.qtd_ok + m.qtd_refugo > 0 ? `${(m.qtd_refugo / (m.qtd_ok + m.qtd_refugo) * 100).toFixed(1)}%` : '—'}
                  </td>
                </tr>
              ))}
              {oee.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-ink-500">Sem dados de OEE no período</td></tr>
              )}
            </tbody>
          </table>
        )}
      </section>

      {/* ETA */}
      <section className="bg-white rounded-lg border border-ink-200">
        <header className="px-4 py-3 border-b border-ink-200 flex items-center gap-2">
          <Clock className="h-4 w-4 text-blue-600" />
          <h2 className="font-semibold">Previsão de fim das OPs (ETA)</h2>
          <span className="text-xs text-ink-500">cascata por máquina considerando fila atual</span>
        </header>
        {etaL ? <Loading /> : (
          <table className="w-full text-sm">
            <thead className="bg-ink-50">
              <tr className="text-left text-xs uppercase text-ink-600">
                <th className="px-3 py-2">OP</th>
                <th className="px-3 py-2">Produto</th>
                <th className="px-3 py-2">Máquina</th>
                <th className="px-3 py-2 text-right">Restante</th>
                <th className="px-3 py-2 text-right">Tempo est.</th>
                <th className="px-3 py-2">Início prev.</th>
                <th className="px-3 py-2">Fim prev.</th>
                <th className="px-3 py-2 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {eta.map(o => (
                <tr key={o.op_id} className={o.em_atraso ? 'bg-red-50' : ''}>
                  <td className="px-3 py-2 font-mono text-xs">#{o.op_numero}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium line-clamp-1 text-xs">{o.produto_descricao}</div>
                    {o.produto_codigo && <div className="text-xs font-mono text-ink-500">{o.produto_codigo}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">{o.maquina_codigo || '—'}</td>
                  <td className="px-3 py-2 text-right text-xs">{(o.quantidade - o.quantidade_produzida).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2 text-right text-xs">
                    {o.tempo_estimado_min > 0 ? formatMinutes(o.tempo_estimado_min) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">{new Date(o.inicio_previsto).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="px-3 py-2 text-xs">
                    {new Date(o.fim_previsto).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    {o.em_atraso && <span className="ml-1 text-red-600 text-[10px] font-bold">⚠ ATRASO</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded ${o.status === 'em_producao' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}>{o.status}</span>
                  </td>
                </tr>
              ))}
              {eta.length === 0 && (
                <tr><td colSpan={8} className="text-center py-6 text-ink-500">Sem OPs em fila</td></tr>
              )}
            </tbody>
          </table>
        )}
      </section>

      {/* CAPACIDADE DETALHADA — TEÓRICA + REAL */}
      <section className="bg-white rounded-lg border border-ink-200">
        <header className="px-4 py-3 border-b border-ink-200 flex items-center gap-2">
          <Factory className="h-4 w-4 text-emerald-600" />
          <h2 className="font-semibold">Capacidade por máquina</h2>
          <span className="text-xs text-ink-500">teórica × real (descontando disponibilidade média 7d)</span>
        </header>
        {capL ? <Loading /> : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50">
              <tr className="text-left text-xs uppercase text-ink-600">
                <th rowSpan={2} className="px-3 py-2 border-r border-ink-100">Máquina</th>
                <th rowSpan={2} className="px-3 py-2 text-right border-r border-ink-100">kg/h</th>
                <th rowSpan={2} className="px-3 py-2 text-right border-r border-ink-100">h/dia</th>
                <th rowSpan={2} className="px-3 py-2 text-right border-r border-ink-100">Disp%</th>
                <th colSpan={3} className="px-3 py-1 text-center bg-violet-50 text-violet-800 border-r border-ink-100">Capacidade TEÓRICA</th>
                <th colSpan={3} className="px-3 py-1 text-center bg-emerald-50 text-emerald-800">Capacidade REAL</th>
              </tr>
              <tr className="text-right text-[10px] uppercase text-ink-500">
                <th className="px-3 py-1 bg-violet-50">kg/dia</th>
                <th className="px-3 py-1 bg-violet-50">kg/semana</th>
                <th className="px-3 py-1 bg-violet-50 border-r border-ink-100">kg/mês</th>
                <th className="px-3 py-1 bg-emerald-50">kg/dia</th>
                <th className="px-3 py-1 bg-emerald-50">kg/semana</th>
                <th className="px-3 py-1 bg-emerald-50">kg/mês</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {cap.map(m => (
                <tr key={m.maquina_id}>
                  <td className="px-3 py-2.5 border-r border-ink-100">
                    <div className="font-medium">{m.codigo}</div>
                    <div className="text-xs text-ink-500">{m.nome} · {m.tipo}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right border-r border-ink-100">{m.capacidade_hora_kg.toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2.5 text-right border-r border-ink-100">{m.horas_dia}</td>
                  <td className={`px-3 py-2.5 text-right font-bold border-r border-ink-100 ${
                    m.disponibilidade_pct >= 85 ? 'text-emerald-700' :
                    m.disponibilidade_pct >= 60 ? 'text-amber-700' : 'text-red-700'
                  }`}>{m.disponibilidade_pct.toFixed(1)}%</td>
                  <td className="px-3 py-2.5 text-right text-violet-700">{Math.round(m.capacidade_dia_kg).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2.5 text-right text-violet-700">{Math.round(m.capacidade_semana_kg).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2.5 text-right text-violet-700 font-medium border-r border-ink-100">{Math.round(m.capacidade_mes_kg).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2.5 text-right text-emerald-700">{Math.round(m.capacidade_real_dia_kg).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2.5 text-right text-emerald-700">{Math.round(m.capacidade_real_semana_kg).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2.5 text-right text-emerald-700 font-medium">{Math.round(m.capacidade_real_mes_kg).toLocaleString('pt-BR')}</td>
                </tr>
              ))}
              {cap.length === 0 && (
                <tr><td colSpan={10} className="text-center py-6 text-ink-500">Sem máquinas ATIVAS cadastradas</td></tr>
              )}
              {cap.length > 0 && (
                <tr className="bg-ink-50 font-bold">
                  <td className="px-3 py-2 border-r border-ink-100">TOTAL</td>
                  <td colSpan={3} className="px-3 py-2 text-right text-xs text-ink-500 border-r border-ink-100">
                    Dias úteis: {cap[0].dias_uteis_semana}/sem · {cap[0].dias_uteis_mes}/mês
                  </td>
                  <td className="px-3 py-2 text-right text-violet-700">{Math.round(cap.reduce((s, m) => s + m.capacidade_dia_kg, 0)).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2 text-right text-violet-700">{Math.round(cap.reduce((s, m) => s + m.capacidade_semana_kg, 0)).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2 text-right text-violet-700 border-r border-ink-100">{Math.round(cap.reduce((s, m) => s + m.capacidade_mes_kg, 0)).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">{Math.round(cap.reduce((s, m) => s + m.capacidade_real_dia_kg, 0)).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">{Math.round(cap.reduce((s, m) => s + m.capacidade_real_semana_kg, 0)).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">{Math.round(cap.reduce((s, m) => s + m.capacidade_real_mes_kg, 0)).toLocaleString('pt-BR')}</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </div>
  )
}

function KPICard({ label, valor, cor, icon: Icon }: { label: string; valor: string; cor: string; icon: any }) {
  const cores: Record<string, string> = {
    violet: 'from-violet-50 to-violet-100 text-violet-700 border-violet-200',
    blue: 'from-blue-50 to-blue-100 text-blue-700 border-blue-200',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-200',
    amber: 'from-amber-50 to-amber-100 text-amber-700 border-amber-200',
  }
  return (
    <div className={`bg-gradient-to-br border rounded-lg p-4 ${cores[cor]}`}>
      <div className="flex items-center justify-between">
        <Icon className="h-5 w-5" />
        <div className="text-2xl font-bold">{valor}</div>
      </div>
      <div className="text-xs uppercase font-semibold mt-1">{label}</div>
    </div>
  )
}

function Bar({ pct, cor }: { pct: number; cor: string }) {
  const cores: Record<string, string> = { blue: 'bg-blue-500', emerald: 'bg-emerald-500', amber: 'bg-amber-500' }
  return (
    <div className="flex items-center gap-2 justify-end">
      <div className="text-xs font-medium w-10 text-right">{pct.toFixed(1)}%</div>
      <div className="w-20 bg-ink-100 rounded-full h-1.5 overflow-hidden">
        <div className={`h-full ${cores[cor]}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  )
}

function classByOee(oee: number): string {
  if (oee >= 85) return 'text-emerald-700'
  if (oee >= 60) return 'text-amber-700'
  return 'text-red-700'
}

function formatMinutes(min: number): string {
  if (min < 60) return `${min}min`
  const h = Math.floor(min / 60), m = min % 60
  return m === 0 ? `${h}h` : `${h}h${m}min`
}

function Loading() {
  return <div className="text-center py-6"><Loader2 className="h-6 w-6 animate-spin text-violet-600 inline" /></div>
}
